import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import Layout from "@/components/layout/Layout";
import IntroReveal from "@/components/IntroReveal";

import Home from "@/pages/Home";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import ThreeWaySystem from "@/pages/ThreeWaySystem";
import Community from "@/pages/Community";
import Programmes from "@/pages/Programmes";
import ProgrammeDetail from "@/pages/ProgrammeDetail";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminProgrammes from "@/pages/admin/AdminProgrammes";
import AdminApplicants from "@/pages/admin/AdminApplicants";
import AdminDispatch from "@/pages/admin/AdminDispatch";
import AdminStories from "@/pages/admin/AdminStories";
import AdminTestShowcase from "@/pages/admin/AdminTestShowcase";
import Stories from "@/pages/Stories";
import Journal from "@/pages/Journal";
import JournalArticle from "@/pages/JournalArticle";
import About from "@/pages/About";
import Faq from "@/pages/Faq";
import PolicyPage from "@/pages/PolicyPage";
import Join from "@/pages/Join";
import Login from "@/pages/Login";
import MyRituals from "@/pages/MyRituals";
import NotFound from "@/pages/NotFound";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function AppRouter() {
  const location = useLocation();

  // Detect OAuth return synchronously (hash fragment) before anything else.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/" element={<Layout><Home /></Layout>} />
      <Route path="/products" element={<Layout><Products /></Layout>} />
      <Route path="/products/:slug" element={<Layout><ProductDetail /></Layout>} />
      <Route path="/three-way-system" element={<Layout><ThreeWaySystem /></Layout>} />
      <Route path="/community" element={<Layout><Community /></Layout>} />
      <Route path="/programmes" element={<Layout><Programmes /></Layout>} />
      <Route path="/programmes/:slug" element={<Layout><ProgrammeDetail /></Layout>} />
      <Route path="/stories" element={<Layout><Stories /></Layout>} />
      <Route path="/journal" element={<Layout><Journal /></Layout>} />
      <Route path="/journal/:slug" element={<Layout><JournalArticle /></Layout>} />
      <Route path="/about" element={<Layout><About /></Layout>} />
      <Route path="/faq" element={<Layout><Faq /></Layout>} />
      <Route path="/privacy" element={<Layout><PolicyPage kind="privacy" /></Layout>} />
      <Route path="/terms" element={<Layout><PolicyPage kind="terms" /></Layout>} />
      <Route path="/join" element={<Join />} />
      <Route path="/login" element={<Login />} />
      <Route
        path="/my-rituals"
        element={<ProtectedRoute><Layout><MyRituals /></Layout></ProtectedRoute>}
      />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminOverview />} />
        <Route path="programmes" element={<AdminProgrammes />} />
        <Route path="applicants" element={<AdminApplicants />} />
        <Route path="dispatch" element={<AdminDispatch />} />
        <Route path="stories" element={<AdminStories />} />
        <Route path="test-showcase" element={<AdminTestShowcase />} />
      </Route>
      <Route path="*" element={<Layout><NotFound /></Layout>} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <IntroReveal />
          <AppRouter />
          <Toaster position="top-center" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
